import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";

import TopNav from "../components/navigation/TopNav";
import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Card from "../components/ui/Card";
import KeyValueList from "../components/ui/KeyValueList";
import FormField from "../components/forms/FormField";

const shellStyle = {
  width: "100%",
  maxWidth: "760px",
  margin: "0 auto",
};

const errorTextStyle = {
  color: "var(--color-danger)",
};

const titleTextStyle = {
  margin: 0,
  fontSize: "var(--font-size-xl)",
  fontWeight: "var(--font-weight-bold)",
  color: "var(--color-text)",
};

const bodyTextStyle = {
  margin: 0,
  color: "var(--color-text)",
};

const mutedTextStyle = {
  margin: 0,
  color: "var(--color-text-muted)",
};

const listStyle = {
  margin: 0,
  paddingLeft: "1.25rem",
  color: "var(--color-text)",
};

const linkStyle = {
  color: "var(--color-accent)",
  textDecoration: "none",
  fontWeight: "var(--font-weight-semibold)",
};

const disabledInputStyle = {
  width: "100%",
  minHeight: "40px",
  padding: "0 12px",
  border: "1px solid var(--color-border, #d0d7de)",
  borderRadius: "8px",
  backgroundColor: "var(--color-surface-muted, #f6f8fa)",
  color: "var(--color-text, inherit)",
  fontSize: "var(--font-size-md)",
  opacity: 1,
};

const MySummary = () => {
  const { t } = useTranslation();
  const { user, loadingUser } = useAuth();

  const [summary, setSummary] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [pendingBills, setPendingBills] = useState([]);
  const [error, setError] = useState(null);

  const token = localStorage.getItem("token");

  const headers = useMemo(() => {
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setError(null);

        const excursionId = user?.excursion?.excursion_id;
        if (!excursionId) {
          setSummary(null);
          setMyStats(null);
          setPendingBills([]);
          return;
        }

        const requests = [
          axios.get(
            `${process.env.REACT_APP_API_URL}/excursions/${excursionId}/summary`,
            { headers }
          ),
          axios.get(
            `${process.env.REACT_APP_API_URL}/bills/current-user-summary/${excursionId}`,
            { headers }
          ),
        ];

requests.push(
  axios.get(
    `${process.env.REACT_APP_API_URL}/temp-bills/pending`,
    { headers }
  )
);

        const responses = await Promise.all(requests);

        const summaryRes = responses[0];
        const myStatsRes = responses[1];
        const pendingBillsRes = responses[2];

        setSummary(summaryRes.data);
        setMyStats(myStatsRes.data);

if (pendingBillsRes) {
  const filteredBills = Array.isArray(pendingBillsRes.data)
    ? pendingBillsRes.data.filter(
        (bill) =>
          String(bill.excursion_id) === String(excursionId)
      )
    : [];

  setPendingBills(filteredBills);
} else {
  setPendingBills([]);
}
      } catch (err) {
        console.error(err);
        setError(t("user_stats.errors.load_failed"));
      }
    };

    if (user && token) {
      fetchStats();
    }
  }, [user, headers, token, t]);

  if (loadingUser) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("user_stats.title")} mode="main" />}
        isLoading
      />
    );
  }

  if (!user) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("user_stats.title")} mode="main" />}
        isError
        errorState={
          <div style={errorTextStyle}>
            {t("user_stats.errors.no_session")}
          </div>
        }
      />
    );
  }

  if (error) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("user_stats.title")} mode="main" />}
        isError
        errorState={<div style={errorTextStyle}>{error}</div>}
      />
    );
  }

  const isAdmin = user.role === "admin";
  const subscriptionStatus = String(user.subscription_status || "");

  const selectedExcursionLabel = user?.excursion
    ? `${user.excursion.excursion_id} - ${user.excursion.short_name}${
        user.excursion.start_date && user.excursion.end_date
          ? ` (${user.excursion.start_date} ${t("user_stats.common.to")} ${user.excursion.end_date})`
          : ""
      }`
    : t("user_stats.labels.no_excursion");

  if (!isAdmin && subscriptionStatus === "pending") {
    return (
      <StandardScreen topNav={<TopNav title={t("user_stats.title")} mode="main" />}>
        <ScreenContent>
          <div style={shellStyle}>
            <Card>
              <Stack gap="4">
                <h2 style={titleTextStyle}>
                  {t("user_stats.greeting", {
                    name: user.name,
                    surname: user.surname,
                  })}
                </h2>

                <FormField label={t("user_stats.fields.excursion")}>
                  <input
                    type="text"
                    value={selectedExcursionLabel}
                    disabled
                    readOnly
                    style={disabledInputStyle}
                  />
                </FormField>

                <p style={bodyTextStyle}>
                  {t("user_stats.messages.welcome", {
                    excursion:
                      user.excursion?.short_name ||
                      t("user_stats.labels.this_excursion"),
                  })}
                </p>

                <p style={mutedTextStyle}>
                  {t("user_stats.messages.pending")}
                </p>
              </Stack>
            </Card>
          </div>
        </ScreenContent>
      </StandardScreen>
    );
  }

  const allowedStatuses = ["approved", "suspended"];
  const allowedMember = allowedStatuses.includes(subscriptionStatus);

  if (!isAdmin && !allowedMember) {
    return (
      <StandardScreen topNav={<TopNav title={t("user_stats.title")} mode="main" />}>
        <ScreenContent>
          <div style={shellStyle}>
            <Card variant="accent">
              <strong>{t("user_stats.errors.no_access")}</strong>
            </Card>
          </div>
        </ScreenContent>
      </StandardScreen>
    );
  }

  return (
    <StandardScreen topNav={<TopNav title={t("user_stats.title")} mode="main" />}>
      <ScreenContent>
        <div style={shellStyle}>
          {myStats ? (
            <Stack gap="5">
              {isAdmin && Number(summary?.pending_members_count || 0) > 0 ? (
                <Card variant="accent">
                  <Link to="/manage-members" style={linkStyle}>
                    {t("user_stats.messages.new_members")}
                  </Link>
                </Card>
              ) : null}

              {pendingBills.length > 0 ? (
                <Card variant="accent">
                  <Link to="/manage-bills" style={linkStyle}>
                  {
                    pendingBills.length === 1
                      ? t("user_stats.messages.pending_bills_message_one", {
                          count: pendingBills.length,
                        })
                      : t("user_stats.messages.pending_bills_message_many", {
                          count: pendingBills.length,
                        })
                  }
                  </Link>
                </Card>
              ) : null}

              <Card>
                <FormField label={t("user_stats.fields.excursion")}>
                  <input
                    type="text"
                    value={selectedExcursionLabel}
                    disabled
                    readOnly
                    style={disabledInputStyle}
                  />
                </FormField>
              </Card>

              <Card
                title={t("user_stats.sections.totals")}
                subtitle={t("user_stats.sections.totals_sub")}
              >
                <KeyValueList
                  items={[
                    {
                      label: t("user_stats.stats.bill_count"),
                      value: summary?.number_of_bills ?? 0,
                    },
                    {
                      label: t("user_stats.stats.total_expense"),
                      value: summary?.total_value_of_bills ?? "0.00",
                      align: "center",
                    },
                    {
                      label: t("user_stats.stats.participants"),
                      value: summary?.number_of_subscribed_members ?? 0,
                      align: "center",
                    },
                  ]}
                />
              </Card>

              <Card
                title={t("user_stats.sections.my_contribution")}
                subtitle={t("user_stats.sections.my_contribution_sub")}
              >
                <KeyValueList
                  items={[
                    {
                      label: t("user_stats.stats.paid_count"),
                      value: myStats?.paid?.bill_count ?? 0,
                    },
                    {
                      label: t("user_stats.stats.benefited_count"),
                      value: myStats?.benefited?.bill_count ?? 0,
                    },
                    {
                      label: t("user_stats.stats.paid_total"),
                      value:
                        typeof myStats?.paid?.total_amount === "number"
                          ? myStats.paid.total_amount.toFixed(2)
                          : "0.00",
                      align: "center",
                    },
                    {
                      label: t("user_stats.stats.benefited_total"),
                      value:
                        typeof myStats?.benefited?.total_amount === "number"
                          ? myStats.benefited.total_amount.toFixed(2)
                          : "0.00",
                      align: "center",
                    },
                  ]}
                />
              </Card>

              <Card title={t("user_stats.sections.settlement")}>
                <Stack gap="3">
                  {myStats?.net?.direction === "receivable" ? (
                    <>
                      <strong>{t("user_stats.settlement.receive")}</strong>

                      {myStats?.settlement?.to_receive?.length > 0 ? (
                        <ul style={listStyle}>
                          {myStats.settlement.to_receive.map((item, idx) => (
                            <li key={idx}>
                              {item.from_name}:{" "}
                              {typeof item.amount === "number"
                                ? item.amount.toFixed(2)
                                : "0.00"}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p style={mutedTextStyle}>{t("user_statscommon.nobody")}</p>
                      )}
                    </>
                  ) : null}

                  {myStats?.net?.direction === "payable" ? (
                    <>
                      <strong>{t("user_stats.settlement.pay")}</strong>

                      {myStats?.settlement?.to_pay?.length > 0 ? (
                        <ul style={listStyle}>
                          {myStats.settlement.to_pay.map((item, idx) => (
                            <li key={idx}>
                              {item.to_name}:{" "}
                              {typeof item.amount === "number"
                                ? item.amount.toFixed(2)
                                : "0.00"}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p style={mutedTextStyle}>{t("user_stats.common.nobody")}</p>
                      )}
                    </>
                  ) : null}

                  {myStats?.net?.direction === "settled" ? (
                    <p style={bodyTextStyle}>
                      {t("user_stats.settlement.settled")}
                    </p>
                  ) : null}
                </Stack>
              </Card>
            </Stack>
          ) : (
            <Card>{t("user_stats.loading")}</Card>
          )}
        </div>
      </ScreenContent>
    </StandardScreen>
  );
};

export default MySummary;